import chatMessageTextStyles from 'css/src/components/agent/ChatMessageText.module.scss';

import React, { memo, useMemo } from 'react';

import type { CustomRendererComponentProps } from 'js/src/agent/types';
import { SketchWidget } from 'js/src/components/sketch/follow-on-actions/SketchWidget';
import { VideoPlanEditor } from 'js/src/components/video-planner/VideoPlanEditor';
import type {
    VideoPlanWithScript,
    VideoPlanWithVoiceoverAsset,
} from 'js/src/data/video-planner/video-planner-types';

type VideoPlanResult = { videoPlan: VideoPlanWithScript | VideoPlanWithVoiceoverAsset };

const DEFAULT_MESSAGE =
    "Here's your video plan. Adjust the script or settings if needed, then click Create video.";

const VideoPlanEditorRendererComponent: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
    toolCall,
}) => {
    if (!toolCall) throw new Error('VideoPlanEditorRenderer requires a toolCall');

    const message = useMemo(() => {
        try {
            const args = JSON.parse(toolCall.function.arguments);
            return args.message || DEFAULT_MESSAGE;
        } catch {
            return DEFAULT_MESSAGE;
        }
    }, [toolCall.function.arguments]);

    const parsedResult = useMemo((): VideoPlanResult | null => {
        if (!result) return null;
        try {
            return JSON.parse(result) as VideoPlanResult;
        } catch {
            return null;
        }
    }, [result]);

    if (!parsedResult && !isLoading) {
        return null;
    }

    return (
        <>
            <div className={`${chatMessageTextStyles['chat-message-text']} mb-l5-8`}>
                <p>{message}</p>
            </div>
            {parsedResult ? (
                <VideoPlanEditor videoPlan={parsedResult.videoPlan} />
            ) : (
                <SketchWidget
                    title="Video outline"
                    titleClassName="font-l5 font-l5--subtitle-l--semibold color-grey-600"
                    isLoading
                />
            )}
        </>
    );
};

export const VideoPlanEditorRenderer = memo(VideoPlanEditorRendererComponent);
